import React from 'react';
// FIX: Use relative path to resolve types module.
import { View, UserSettings } from '../types';
import { UI_CONFIG, HOMEPAGE_CONFIG, FEATURE_FLAGS, resolvePublicFileUrl, getHomepageToolMediaEntry } from '../config';
import { ChevronRightIcon, ChatBubbleLeftRightIcon, PaintBrushIcon, BookOpenIcon, AcademicCapIcon, DocumentTextIcon, QuestionMarkCircleIcon, BoltIcon, PresentationChartBarIcon, ClipboardDocumentListIcon, PuzzlePieceIcon, SparklesIcon, CheckIcon, ScrollIcon, BeakerIcon, ChatBubbleBottomCenterTextIcon, PlayIcon, Square2StackIcon, UserGroupIcon, PhotoIcon, TrophyIcon } from './common/Icons';
import Card from './common/Card';
import { AdSlot } from './common/AdSlot';
import LoadingDataIndicator from './common/LoadingDataIndicator';

interface DashboardProps {
  onNavigate: (view: View, payload?: object) => void;
  user: UserSettings | null;
  onUpdateSettings?: (update: React.SetStateAction<UserSettings | null>) => void;
  isLoading?: boolean;
}

const IconMap: Record<string, any> = {
    DocumentTextIcon: DocumentTextIcon,
    PresentationChartBarIcon: PresentationChartBarIcon,
    Square2StackIcon: Square2StackIcon,
    QuestionMarkCircleIcon: QuestionMarkCircleIcon,
    BoltIcon: BoltIcon,
    PuzzlePieceIcon: PuzzlePieceIcon,
    ClipboardDocumentListIcon: ClipboardDocumentListIcon,
    SparklesIcon: SparklesIcon,
    AcademicCapIcon: AcademicCapIcon,
    PlayIcon: PlayIcon,
    UserGroupIcon: UserGroupIcon,
    CheckIcon: CheckIcon,
};

const getIcon = (name: string, className: string = "h-5 w-5") => {
    const Icon = IconMap[name] || SparklesIcon;
    return <Icon className={className} />;
};

/** Sắp xếp danh sách công cụ theo thứ tự key trong JSON admin (Object.keys — thứ tự lưu khi kéo ô trong cấu hình). */
function orderToolsByConfigKeys<T extends { view: View }>(tools: T[], media: Record<string, unknown> | undefined): T[] {
    const keys = Object.keys(media || {});
    if (keys.length === 0) return tools;
    const defaultIndex = new Map(tools.map((t, idx) => [t.view, idx]));
    const FALLBACK = 1000;
    return [...tools].sort((a, b) => {
        const ia = keys.indexOf(String(a.view));
        const ib = keys.indexOf(String(b.view));
        const ra = ia === -1 ? FALLBACK + (defaultIndex.get(a.view) ?? 0) : ia;
        const rb = ib === -1 ? FALLBACK + (defaultIndex.get(b.view) ?? 0) : ib;
        return ra - rb;
    });
}

/** 4 avatar cho dòng chứng thực; URL từ admin hoặc pravatar mẫu */
function buildSocialAvatarUrls(configUrls: string[] | undefined, fallbackPrefix: string): string[] {
    const cleaned = (configUrls || [])
        .filter((u) => typeof u === 'string' && u.trim().length > 0)
        .map((u) => resolvePublicFileUrl(u.trim()));
    const count = 4;
    const out: string[] = [];
    for (let i = 0; i < count; i++) {
        if (cleaned[i]) out.push(cleaned[i]);
        else out.push(`https://i.pravatar.cc/100?u=${encodeURIComponent(`${fallbackPrefix}-${i}`)}`);
    }
    return out;
}

/** Chỉ cuộn strip overflow-x; không dùng scrollIntoView (tránh kéo cả trang về khối slider). */
function scrollTabStripToActiveButton(
    strip: HTMLElement | null,
    button: HTMLElement | null,
    behavior: ScrollBehavior = 'smooth',
) {
    if (!strip || !button) return;
    const stripRect = strip.getBoundingClientRect();
    const btnRect = button.getBoundingClientRect();
    const btnCenter = btnRect.left + btnRect.width / 2;
    const stripCenter = stripRect.left + stripRect.width / 2;
    const delta = btnCenter - stripCenter;
    const maxScroll = Math.max(0, strip.scrollWidth - strip.clientWidth);
    const next = strip.scrollLeft + delta;
    strip.scrollTo({ left: Math.max(0, Math.min(next, maxScroll)), behavior });
}

const FunctionCard: React.FC<{
    title: string;
    description: string;
    icon: React.ReactNode;
    view: View;
    onNavigate: (view: View) => void;
    comingSoon?: boolean;
    media?: string[];
}> = ({ title, description, icon, view, onNavigate, comingSoon, media }) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [isFullscreenOpen, setIsFullscreenModalOpen] = React.useState(false);
    const hasMedia = media && media.length > 0;

    return (
        <>
            <div 
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                className={`flex flex-col h-full min-h-[320px] rounded-2xl relative overflow-hidden border transition-all duration-500 group cursor-pointer ${
                    comingSoon 
                        ? 'bg-slate-50/80 border-slate-200' 
                        : 'bg-white border-slate-100 shadow-md hover:shadow-2xl hover:shadow-teal-500/20 hover:-translate-y-2 hover:border-teal-200/50'
                }`}
            >
                {!comingSoon && title.includes('Mới') && (
                    <div className="absolute top-0 right-0 z-20">
                        <div className="bg-gradient-to-r from-rose-500 to-amber-500 text-white text-[10px] font-black px-3 py-1.5 rounded-bl-xl uppercase tracking-widest shadow-lg">
                            Mới
                        </div>
                    </div>
                )}

                {hasMedia && isHovered && (
                    <div className="absolute top-4 right-4 z-20">
                        <button 
                            onClick={(e) => { e.stopPropagation(); setIsFullscreenModalOpen(true); }}
                            className="p-2 bg-white/90 backdrop-blur-md rounded-full shadow-lg text-slate-600 hover:text-teal-600 transition-colors"
                        >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                        </button>
                    </div>
                )}

                <div className="flex-1 flex flex-col relative h-full">
                    {/* First Slide: Info */}
                    <div className={`p-8 space-y-4 transition-all duration-500 flex flex-col h-full ${hasMedia && isHovered ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
                        <div className={`h-14 w-14 flex items-center justify-center rounded-2xl transition-all duration-500 ${
                            comingSoon ? 'bg-slate-200 text-slate-400' : 'bg-slate-50 text-slate-600 group-hover:bg-teal-50 group-hover:text-teal-600 shadow-sm group-hover:shadow-inner'
                        }`}>
                            {icon}
                        </div>
                        <h3 className={`text-xl font-black leading-tight tracking-tight ${comingSoon ? 'text-slate-500' : 'text-slate-800 group-hover:text-teal-700'}`}>{title}</h3>
                        <p className={`text-sm font-medium leading-relaxed flex-1 ${comingSoon ? 'text-slate-400' : 'text-slate-500 group-hover:text-slate-600'}`}>{description}</p>
                    </div>

                    {/* Media Slide (Hover) */}
                    {hasMedia && (
                        <div className={`absolute inset-0 transition-all duration-500 ${isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
                            <MediaCarousel media={media} className="h-full rounded-none" />
                        </div>
                    )}
                </div>

                <button 
                    onClick={() => !comingSoon && onNavigate(view)} 
                    className={`mt-auto w-full text-left px-8 py-5 rounded-b-2xl text-sm font-black uppercase tracking-[0.2em] flex items-center justify-between transition-all duration-300 border-t ${
                        comingSoon 
                            ? 'bg-slate-50 border-slate-100 text-slate-400 cursor-not-allowed'
                            : 'bg-white border-slate-50 text-teal-600 group-hover:bg-teal-600 group-hover:text-white group-hover:border-teal-600'
                    }`}
                    disabled={comingSoon}
                >
                    <span>{comingSoon ? 'Sắp ra mắt' : 'Khám phá ngay'}</span>
                    {!comingSoon && <ChevronRightIcon className="h-5 w-5 transform group-hover:translate-x-1 transition-transform" />}
                </button>
            </div>

            {hasMedia && (
                <FullscreenModal 
                    isOpen={isFullscreenOpen} 
                    onClose={() => setIsFullscreenModalOpen(false)} 
                    title={title}
                >
                    <div className="grid lg:grid-cols-[1fr,350px] gap-12">
                        <div className="space-y-6">
                            <MediaCarousel media={media} className="aspect-video rounded-3xl shadow-2xl" />
                            <div className="flex gap-4">
                                {media.map((url, i) => (
                                    <div key={i} className="w-24 aspect-video rounded-xl overflow-hidden border-2 border-slate-100 hover:border-teal-500 cursor-pointer transition-all shadow-sm">
                                        <img src={resolvePublicFileUrl(url)} className="w-full h-full object-cover" alt={`Thumb ${i}`} />
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="space-y-8 flex flex-col justify-center">
                            <div className="space-y-4">
                                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 text-teal-700 text-[10px] font-black uppercase tracking-widest border border-teal-100">
                                    Tính năng thông minh
                                </div>
                                <h2 className="text-4xl font-black text-slate-900 leading-tight">{title}</h2>
                                <p className="text-lg text-slate-500 font-medium leading-relaxed">{description}</p>
                            </div>
                            
                            <div className="space-y-4">
                                <h4 className="font-black text-slate-800 uppercase tracking-widest text-xs">Lợi ích nổi bật:</h4>
                                <ul className="space-y-3">
                                    {['Tiết kiệm thời gian soạn thảo', 'Nội dung chuẩn kiến thức kỹ năng', 'Trực quan và lôi cuốn học sinh'].map((item, i) => (
                                        <li key={i} className="flex items-center gap-3 text-slate-600 font-bold">
                                            <div className="h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 shrink-0"><CheckIcon className="h-3 w-3" /></div>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="pt-6">
                                <button 
                                    onClick={() => { setIsFullscreenModalOpen(false); onNavigate(view); }}
                                    className="w-full py-5 bg-teal-600 text-white font-black rounded-2xl shadow-xl shadow-teal-600/20 hover:bg-teal-700 transition-all transform hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-3 text-lg"
                                >
                                    <span>Bắt đầu trải nghiệm</span>
                                    <ChevronRightIcon className="h-6 w-6" />
                                </button>
                            </div>
                        </div>
                    </div>
                </FullscreenModal>
            )}
        </>
    );
};

// --- Mockup Components for Visual Appeal ---

const FullscreenModal: React.FC<{ 
    isOpen: boolean; 
    onClose: () => void; 
    title: string; 
    children: React.ReactNode 
}> = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 backdrop-blur-md p-4" onClick={onClose}>
            <div className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-[32px] overflow-hidden shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="text-2xl font-black text-slate-800 tracking-tight">{title}</h3>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-400 hover:text-slate-600">
                        <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div className="flex-1 overflow-auto p-8">
                    {children}
                </div>
            </div>
        </div>
    );
};

/** Ảnh minh họa thực tế (không khung trắng) khi FEATURE_SHOWCASE chưa cấu hình media.
 *  Đặt tại /assets/images/dashboard/feature-showcase — xem README cùng thư mục để biết prompt sinh ảnh. */
const FEATURE_SHOWCASE_FALLBACK_IMAGES = [
    '/assets/images/dashboard/feature-showcase/lesson-plan.webp',
    '/assets/images/dashboard/feature-showcase/slide.webp',
    '/assets/images/dashboard/feature-showcase/simulation-3d.webp',
    '/assets/images/dashboard/feature-showcase/quiz.webp',
] as const;

const FEATURE_SHOWCASE_DEFAULT_VIEWS: View[] = [
    View.LessonPlanCreator,
    View.SlideCreator,
    View.ThietKe3DCreator,
    View.QuizCreator,
];

const FEATURE_SHOWCASE_FALLBACK_IMAGE_BY_VIEW: Partial<Record<View, string>> = {
    [View.LessonPlanCreator]: FEATURE_SHOWCASE_FALLBACK_IMAGES[0],
    [View.SlideCreator]: FEATURE_SHOWCASE_FALLBACK_IMAGES[1],
    [View.ThietKe3DCreator]: FEATURE_SHOWCASE_FALLBACK_IMAGES[2],
    [View.QuizCreator]: FEATURE_SHOWCASE_FALLBACK_IMAGES[3],
};

const isKnownView = (value: unknown): value is View => Object.values(View).includes(value as View);

const renderFeatureShowcaseMockup = (view: View) => {
    switch (view) {
        case View.LessonPlanCreator:
            return <LessonPlanMockup />;
        case View.SlideCreator:
            return <SlideMockup />;
        case View.ThietKe3DCreator:
            return (
                <MockupWindow title="3D Simulation & VR" className="h-80 md:h-[420px] bg-slate-950 border-white/10">
                    <div className="h-full flex flex-col items-center justify-center p-12 space-y-8 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] relative overflow-hidden">
                        <div className="absolute inset-0 bg-gradient-to-t from-teal-500/20 to-transparent" />
                        <div className="h-32 w-32 rounded-full bg-teal-500/10 border-4 border-teal-500/40 flex items-center justify-center animate-spin-slow relative">
                            <Square2StackIcon className="h-16 w-16 text-teal-400 drop-shadow-[0_0_15px_rgba(45,212,191,0.5)]" />
                        </div>
                        <div className="text-center relative z-10">
                            <div className="text-2xl font-black text-white tracking-tight">Hệ Mặt Trời 3D</div>
                            <div className="text-sm text-teal-400 font-bold mt-2 uppercase tracking-[0.2em]">Mô phỏng tương tác</div>
                        </div>
                    </div>
                </MockupWindow>
            );
        case View.QuizCreator:
            return (
                <MockupWindow title="Smart Quiz Engine" className="h-80 md:h-[420px] bg-purple-50/50">
                    <div className="p-8 space-y-6 h-full flex flex-col">
                        <div className="space-y-3">
                            <div className="h-3 w-1/4 bg-purple-600 rounded-full" />
                            <div className="h-4 w-full bg-slate-800 rounded-full" />
                            <div className="h-4 w-2/3 bg-slate-800 rounded-full" />
                        </div>
                        <div className="space-y-3 flex-1">
                            {[1, 2, 3].map(i => (
                                <div key={i} className={`p-4 rounded-xl bg-white border-2 transition-all flex items-center gap-3 ${i === 2 ? 'border-emerald-500 bg-emerald-50/50' : 'border-slate-100'}`}>
                                    <div className={`h-6 w-6 rounded-full border-2 flex items-center justify-center font-black text-xs ${i === 2 ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-300'}`}>
                                        {i === 2 ? <CheckIcon className="h-4 w-4" /> : String.fromCharCode(64 + i)}
                                    </div>
                                    <div className={`h-2.5 w-full rounded-full ${i === 2 ? 'bg-emerald-200' : 'bg-slate-100'}`} />
                                </div>
                            ))}
                        </div>
                    </div>
                </MockupWindow>
            );
        default:
            return <LessonPlanMockup />;
    }
};

const MediaCarousel: React.FC<{
    media: string[];
    className?: string;
    bleed?: boolean;
    /** Lớp phủ (gradient…) nằm trên ảnh, dưới nút/chấm điều hướng */
    overlay?: React.ReactNode;
}> = ({ media, className = '', bleed = false, overlay = null }) => {
    const [current, setCurrent] = React.useState(0);
    
    if (!media || media.length === 0) return null;

    const next = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrent((prev) => (prev + 1) % media.length);
    };
    const prev = (e: React.MouseEvent) => {
        e.stopPropagation();
        setCurrent((prev) => (prev - 1 + media.length) % media.length);
    };

    const isVideo = (url: string) => url.match(/\.(mp4|webm|ogg)$/) || url.includes('youtube.com') || url.includes('youtu.be');

    return (
        <div className={`relative group/carousel overflow-hidden bg-slate-900 ${bleed ? 'rounded-none' : 'rounded-xl'} ${className}`}>
            <div className="absolute inset-0 flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${current * 100}%)` }}>
                {media.map((url, i) => {
                    const isActive = i === current;
                    return (
                    <div
                        key={i}
                        className="min-w-full h-full relative overflow-hidden bg-slate-900"
                        aria-hidden={isActive ? undefined : true}
                    >
                        {!bleed && !isVideo(url) && (
                            <div 
                                className="absolute inset-0 scale-125 blur-3xl opacity-40 mix-blend-overlay pointer-events-none"
                                style={{ backgroundImage: `url(${resolvePublicFileUrl(url)})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
                            />
                        )}
                        
                        <div className={`relative z-10 w-full h-full ${bleed ? '' : 'flex items-center justify-center'}`}>
                            {isVideo(url) ? (
                                url.includes('youtube') || url.includes('youtu.be') ? (
                                    <iframe
                                        className="w-full h-full pointer-events-none"
                                        src={`https://www.youtube.com/embed/${url.split('/').pop()?.split('?')[0]}?autoplay=0&mute=1&controls=0`}
                                        title={`Video ${i + 1}`}
                                        loading="lazy"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        frameBorder="0"
                                    />
                                ) : (
                                    <video
                                        src={resolvePublicFileUrl(url)}
                                        className="w-full h-full object-cover object-center"
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload={isActive ? 'metadata' : 'none'}
                                    />
                                )
                            ) : (
                                <img
                                    src={resolvePublicFileUrl(url)}
                                    className={bleed ? 'absolute inset-0 h-full w-full object-cover object-right' : 'w-full max-h-full object-contain shadow-2xl'}
                                    alt={`Slide ${i + 1}`}
                                    loading={i === 0 ? 'eager' : 'lazy'}
                                    decoding="async"
                                    fetchPriority={i === 0 ? 'high' : 'low'}
                                />
                            )}
                        </div>
                    </div>
                    );
                })}
            </div>

            {overlay ? <div className="pointer-events-none absolute inset-0 z-[5]">{overlay}</div> : null}
            
            {media.length > 1 && (
                <>
                    <button type="button" onClick={prev} className={`absolute top-1/2 z-30 -translate-y-1/2 p-2 bg-white/20 backdrop-blur-md rounded-full text-white transition-opacity hover:bg-white/40 ${bleed ? 'left-3 opacity-80 hover:opacity-100' : 'left-2 opacity-0 group-hover/carousel:opacity-100'}`}>
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7" /></svg>
                    </button>
                    <button type="button" onClick={next} className={`absolute top-1/2 z-30 -translate-y-1/2 p-2 bg-white/20 backdrop-blur-md rounded-full text-white transition-opacity hover:bg-white/40 ${bleed ? 'right-3 opacity-80 hover:opacity-100' : 'right-2 opacity-0 group-hover/carousel:opacity-100'}`}>
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M9 5l7 7-7 7" /></svg>
                    </button>
                    <div className={`absolute z-30 flex gap-1.5 ${bleed ? 'bottom-5 right-6' : 'bottom-2 left-1/2 -translate-x-1/2'}`}>
                        {media.map((_, i) => (
                            <div key={i} className={`h-1.5 rounded-full transition-all ${current === i ? 'w-4 bg-white' : 'w-1.5 bg-white/40'}`} />
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

const MockupWindow: React.FC<{ children: React.ReactNode; title?: string; className?: string }> = ({ children, title, className }) => (
    <div className={`rounded-xl border border-slate-200 bg-white shadow-2xl overflow-hidden flex flex-col ${className}`}>
        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2 flex items-center gap-2 shrink-0">
            <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-rose-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-amber-400" />
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
            </div>
            {title && <div className="mx-auto text-[10px] font-bold text-slate-400 uppercase tracking-widest">{title}</div>}
        </div>
        <div className="flex-1 overflow-hidden relative">
            {children}
        </div>
    </div>
);

const LessonPlanMockup = () => (
    <MockupWindow title="AI Lesson Plan Creator" className="h-64 md:h-80">
        <div className="p-4 space-y-4">
            <div className="flex items-center gap-3">
                <div className="h-8 w-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600"><DocumentTextIcon className="h-5 w-5" /></div>
                <div className="h-4 w-40 bg-slate-100 rounded" />
            </div>
            <div className="grid grid-cols-3 gap-3">
                <div className="h-20 bg-slate-50 rounded-lg border border-dashed border-slate-200 flex items-center justify-center">
                    <div className="h-2 w-12 bg-slate-200 rounded" />
                </div>
                <div className="h-20 bg-blue-50 rounded-lg border border-blue-100 p-3 space-y-2">
                    <div className="h-1.5 w-full bg-blue-200 rounded" />
                    <div className="h-1.5 w-2/3 bg-blue-200 rounded" />
                </div>
                <div className="h-20 bg-slate-50 rounded-lg border border-dashed border-slate-200" />
            </div>
            <div className="space-y-2">
                <div className="h-2 w-full bg-slate-100 rounded" />
                <div className="h-2 w-full bg-slate-100 rounded" />
                <div className="h-2 w-4/5 bg-slate-100 rounded" />
            </div>
            <div className="flex justify-end pt-2">
                <div className="px-4 py-2 bg-teal-600 rounded-lg text-[10px] font-bold text-white shadow-lg">GENERATE AI</div>
            </div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent opacity-60 pointer-events-none" />
    </MockupWindow>
);

const SlideMockup = () => (
    <MockupWindow title="AI Slide Presentation" className="h-64 md:h-80">
        <div className="flex h-full">
            <div className="w-16 border-r border-slate-100 bg-slate-50 p-2 space-y-2">
                {[1, 2, 3, 4].map(i => (
                    <div key={i} className={`h-8 rounded ${i === 1 ? 'bg-teal-100 border border-teal-200' : 'bg-white border border-slate-200'}`} />
                ))}
            </div>
            <div className="flex-1 p-6 flex flex-col items-center justify-center text-center space-y-4">
                <div className="w-full max-w-[200px] h-32 rounded-xl bg-gradient-to-br from-teal-500 to-blue-600 shadow-xl flex items-center justify-center">
                    <PresentationChartBarIcon className="h-12 w-12 text-white/50" />
                </div>
                <div className="space-y-2 w-full max-w-[160px]">
                    <div className="h-3 bg-slate-800 rounded mx-auto" />
                    <div className="h-2 bg-slate-400 rounded w-2/3 mx-auto" />
                </div>
            </div>
        </div>
    </MockupWindow>
);

const AdminMockup = () => (
    <MockupWindow title="Admin Mission Control" className="h-64 md:h-80">
        <div className="p-4 flex flex-col h-full">
            <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <div className="text-[10px] font-bold text-slate-500 mb-1">CREDITS USED</div>
                    <div className="text-xl font-black text-teal-600 tracking-tight">854,200</div>
                    <div className="w-full h-1 bg-slate-200 rounded-full mt-2">
                        <div className="w-3/4 h-full bg-teal-500 rounded-full" />
                    </div>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                    <div className="text-[10px] font-bold text-slate-500 mb-1">AI PROVIDER</div>
                    <div className="flex items-center gap-2 mt-1">
                        <div className="h-4 w-4 rounded-full bg-blue-500" />
                        <div className="text-xs font-bold text-slate-700">Gemini 1.5 Pro</div>
                    </div>
                </div>
            </div>
            <div className="flex-1 bg-white border border-slate-200 rounded-lg p-3 space-y-3">
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <div className="h-3 w-20 bg-slate-100 rounded" />
                    <div className="w-8 h-4 bg-teal-100 rounded-full relative">
                        <div className="absolute right-0.5 top-0.5 w-3 h-3 bg-teal-600 rounded-full" />
                    </div>
                </div>
                <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <div className="h-3 w-24 bg-slate-100 rounded" />
                    <div className="w-8 h-4 bg-slate-200 rounded-full relative">
                        <div className="absolute left-0.5 top-0.5 w-3 h-3 bg-slate-400 rounded-full" />
                    </div>
                </div>
                <div className="flex items-center justify-between">
                    <div className="h-3 w-16 bg-slate-100 rounded" />
                    <div className="w-8 h-4 bg-teal-100 rounded-full relative">
                        <div className="absolute right-0.5 top-0.5 w-3 h-3 bg-teal-600 rounded-full" />
                    </div>
                </div>
            </div>
        </div>
    </MockupWindow>
);

const Dashboard: React.FC<DashboardProps> = ({ onNavigate, user, isLoading }) => {
    const [isVideoModalOpen, setIsVideoModalOpen] = React.useState(false);
    const [activeFeatureTab, setActiveFeatureTab] = React.useState(0);
    const [activeTeacherToolTab, setActiveTeacherToolTab] = React.useState(0);
    const [isTeacherSliderPaused, setIsTeacherSliderPaused] = React.useState(false);
    const [activeStudentToolTab, setActiveStudentToolTab] = React.useState(0);
    const [isStudentSliderPaused, setIsStudentSliderPaused] = React.useState(false);
    const homepageYoutubeId = String(HOMEPAGE_CONFIG.YOUTUBE_VIDEO_ID || '').trim();
    const homepageVideoUrl = String(HOMEPAGE_CONFIG.VIDEO_URL || '').trim();
    const heroMockupImage = String(HOMEPAGE_CONFIG.HERO_MOCKUP_IMAGE || '').trim();
    const useHeroMockupImage = heroMockupImage.length > 3;
    const hasHomepageVideo =
        (HOMEPAGE_CONFIG.VIDEO_SOURCE === 'server' && !!homepageVideoUrl) ||
        (HOMEPAGE_CONFIG.VIDEO_SOURCE === 'youtube' && !!homepageYoutubeId);

    const teacherSocialAvatars = buildSocialAvatarUrls(HOMEPAGE_CONFIG.TEACHER_SOCIAL_AVATARS, 'gv');
    const studentSocialAvatars = buildSocialAvatarUrls(HOMEPAGE_CONFIG.STUDENT_SOCIAL_AVATARS, 'hs');
    const teacherSocialCaption = (HOMEPAGE_CONFIG.TEACHER_SOCIAL_CAPTION || '').trim() || '+5,000 Giáo viên tin dùng';
    const studentSocialCaption = (HOMEPAGE_CONFIG.STUDENT_SOCIAL_CAPTION || '').trim() || '+10,000 Học sinh đang học';
    const featureShowcaseTools = ((Array.isArray(HOMEPAGE_CONFIG.FEATURE_SHOWCASE) ? HOMEPAGE_CONFIG.FEATURE_SHOWCASE : []) as any[])
        .map((item, index) => {
            const fallbackView = FEATURE_SHOWCASE_DEFAULT_VIEWS[index] || FEATURE_SHOWCASE_DEFAULT_VIEWS[0];
            const view = isKnownView(item?.view) ? item.view : fallbackView;
            return {
                ...item,
                view,
                fallbackImage: FEATURE_SHOWCASE_FALLBACK_IMAGE_BY_VIEW[view] || FEATURE_SHOWCASE_FALLBACK_IMAGES[index % FEATURE_SHOWCASE_FALLBACK_IMAGES.length],
            };
        });
    const activeFeatureShowcaseTool = featureShowcaseTools[activeFeatureTab] ?? featureShowcaseTools[0];

    const teacherTabBtnRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
    const studentTabBtnRefs = React.useRef<(HTMLButtonElement | null)[]>([]);
    const teacherTabStripRef = React.useRef<HTMLDivElement | null>(null);
    const studentTabStripRef = React.useRef<HTMLDivElement | null>(null);

    const navigateWithLoginRedirect = React.useCallback((view: View, payload?: object) => {
        if (!user) {
            onNavigate(View.Login, { redirect: { view, payload } });
            return;
        }
        onNavigate(view, payload);
    }, [onNavigate, user]);

    const handleStartNow = () => {
        if (!user) {
            onNavigate(View.Login);
            return;
        }
        
        const dashboardMap: Record<string, View> = {
            'teacher': View.TeacherTools,
            'parent': View.ParentDashboard,
            'student': View.StudentDashboard
        };
        
        onNavigate(dashboardMap[user.role] || View.Dashboard);
    };

    const handleFeatureShowcaseStart = React.useCallback(() => {
        if (!activeFeatureShowcaseTool?.view) {
            handleStartNow();
            return;
        }
        navigateWithLoginRedirect(activeFeatureShowcaseTool.view);
    }, [activeFeatureShowcaseTool, navigateWithLoginRedirect]);

    const handleLearningPlanStart = React.useCallback(() => {
        navigateWithLoginRedirect(View.StudentDashboard);
    }, [navigateWithLoginRedirect]);

    const handleAdminStart = React.useCallback(() => {
        navigateWithLoginRedirect(View.TeacherTools);
    }, [navigateWithLoginRedirect]);
    
    const teacherTools = React.useMemo(() => {
        const map = HOMEPAGE_CONFIG.TEACHER_TOOLS_MEDIA as Record<string, unknown> | undefined;
        const raw = [
            { view: View.LessonPlanCreator, title: 'Soạn giáo án 5E chuẩn GDPT', description: 'Tạo giáo án chuyên nghiệp, sáng tạo chỉ trong 30 giây, bám sát khung năng lực và tiêu chuẩn 5E mới nhất.', icon: <DocumentTextIcon className="h-7 w-7 text-blue-500" />, mockup: <LessonPlanMockup /> },
            { view: View.SlideCreator, title: 'Slide bài giảng thông minh', description: 'Biến ý tưởng và tài liệu thành những bài thuyết trình rực rỡ, trực quan, thu hút mọi ánh nhìn của học sinh.', icon: <PresentationChartBarIcon className="h-7 w-7 text-green-500" />, mockup: <SlideMockup /> },
            { view: View.QuizCreator, title: 'Ngân hàng đề thi & Quiz AI', description: 'Tự động hóa việc tạo đề kiểm tra đa dạng, phân hóa trình độ, giúp bạn đánh giá học sinh chính xác và nhanh chóng.', icon: <QuestionMarkCircleIcon className="h-7 w-7 text-purple-500" /> },
            { view: View.ScientificSimulationCreator, title: 'Phòng thí nghiệm ảo', description: 'Mang cả thế giới khoa học vào lớp học với các mô phỏng tương tác sinh động, giúp bài giảng trở nên chân thực hơn bao giờ hết.', icon: <BoltIcon className="h-7 w-7 text-teal-500" /> },
            { view: View.ThietKe3DCreator, title: 'Ứng dụng học tập 3D', description: 'Tự tay thiết kế các mô hình 3D tương tác độc đáo, cho phép học sinh khám phá mọi góc cạnh của tri thức.', icon: <Square2StackIcon className="h-7 w-7 text-indigo-600" /> },
            { view: View.ActivityCreator, title: 'Hoạt động dạy học sáng tạo', description: 'Khơi nguồn cảm hứng với kịch bản, bài hát và câu chuyện giáo dục được AI cá nhân hóa theo từng nội dung bài học.', icon: <PuzzlePieceIcon className="h-7 w-7 text-indigo-500" /> },
            { view: View.HomeworkCreator, title: 'Trợ lý bài tập về nhà', description: 'Tạo bài tập đa phương tiện lôi cuốn, theo dõi tiến độ nộp bài tự động, xóa tan nỗi lo chấm bài thủ công.', icon: <ClipboardDocumentListIcon className="h-7 w-7 text-yellow-500" /> },
            { view: View.OralQuizCreator, title: 'Kiểm tra kiến thức trực tiếp', description: 'Tổ chức các phiên kiểm tra miệng, quiz nhanh ngay trong lớp để tăng tương tác và nắm bắt ngay mức độ hiểu bài.', icon: <SparklesIcon className="h-7 w-7 text-indigo-500" /> },
            { view: View.GameCreator, title: 'Game giáo dục tương tác', description: 'Biến việc ôn tập thành những cuộc phiêu lưu kỳ thú với kho trò chơi trí tuệ, giúp học sinh vừa học vừa chơi cực đỉnh.', icon: <BoltIcon className="h-7 w-7 text-orange-500" /> },
            { view: View.SKKNCreator, title: 'Chuyên gia viết SKKN AI', description: 'Hoàn thiện Sáng kiến kinh nghiệm xuất sắc với cấu trúc logic, ngôn ngữ chuyên nghiệp và những gợi ý sáng tạo từ AI.', icon: <AcademicCapIcon className="h-7 w-7 text-teal-600" /> },
            { view: View.DesignCenter, title: 'Studio thiết kế học liệu', description: 'Tự do sáng tạo poster, thẻ từ vựng và bìa bài giảng đẹp mắt với kho tài nguyên AI vô tận dành riêng cho giáo viên.', icon: <SparklesIcon className="h-7 w-7 text-violet-500" /> },
            { view: View.VideoCreator, title: 'Video bài giảng MC ảo', description: 'Sản xuất video giáo dục chuyên nghiệp với MC ảo sống động và giọng đọc truyền cảm, tối ưu trải nghiệm học tập từ xa.', icon: <PlayIcon className="h-7 w-7 text-red-500" /> },
            { view: View.ClassManagement, title: 'Quản trị lớp học thông minh', description: 'Quản lý hồ sơ, theo dõi sự tiến bộ và chấm điểm bằng AI, giúp bạn thấu hiểu từng học sinh để giảng dạy hiệu quả hơn.', icon: <UserGroupIcon className="h-7 w-7 text-teal-500" /> },
            { view: View.DocumentLibrary, title: 'Kho lưu trữ tri thức số', description: 'Hệ thống lưu trữ thông minh giúp bạn quản lý toàn bộ học liệu, dự án giáo dục một cách khoa học và bảo mật tuyệt đối.', icon: <Square2StackIcon className="h-7 w-7 text-blue-600" /> },
        ]
            .filter((tool) => {
                if (tool.view === View.VideoCreator) return FEATURE_FLAGS.VIDEO_GENERATION !== false;
                if (tool.view === View.DesignCenter) return FEATURE_FLAGS.IMAGE_GENERATION !== false;
                return true;
            })
            .map((tool) => {
                const cfg = getHomepageToolMediaEntry(map, tool.view);
                return {
                    ...tool,
                    media: cfg.urls !== undefined ? cfg.urls : undefined,
                    title: cfg.title ?? tool.title,
                    description: cfg.description ?? tool.description,
                };
            });
        return orderToolsByConfigKeys(raw, map);
    }, [FEATURE_FLAGS.VIDEO_GENERATION, FEATURE_FLAGS.IMAGE_GENERATION, HOMEPAGE_CONFIG.TEACHER_TOOLS_MEDIA]);

    const studentTools = React.useMemo(() => {
        const map = HOMEPAGE_CONFIG.STUDENT_TOOLS_MEDIA as Record<string, unknown> | undefined;
        const raw = [
            { view: View.StudentDashboard, title: 'Lộ trình học tập cá nhân', description: 'Trợ lý AI thiết kế riêng cho bạn con đường chinh phục tri thức tối ưu, bám sát mục tiêu và tốc độ học tập của chính bạn.', icon: <TrophyIcon className="h-7 w-7 text-yellow-500" /> },
            { view: View.EnglishLab, title: 'English Lab - Giao tiếp 24/7', description: 'Luyện nói tiếng Anh tự tin với AI mọi lúc mọi nơi, sửa lỗi tức thì và nâng tầm vốn từ vựng một cách tự nhiên.', icon: <AcademicCapIcon className="h-7 w-7 text-blue-500" /> },
            { view: View.Pronunciation, title: 'Chuyên gia phát âm AI', description: 'Chỉnh sửa phát âm chuẩn xác từng âm tiết, giúp bạn sở hữu giọng đọc bản ngữ chuyên nghiệp qua công nghệ phân tích sóng âm.', icon: <ChatBubbleLeftRightIcon className="h-7 w-7 text-teal-500" /> },
            { view: View.HistoryStoryteller, title: 'Cỗ máy thời gian lịch sử', description: 'Du hành về quá khứ qua những câu chuyện kể sinh động, chân thực, giúp lịch sử không còn là những con số khô khan.', icon: <ScrollIcon className="h-7 w-7 text-amber-600" /> },
            { view: View.LiteratureAssistant, title: 'Trợ lý văn học sáng tạo', description: 'Khơi gợi tư duy phê bình, giúp bạn thấu cảm sâu sắc các tác phẩm văn học qua những góc nhìn mới lạ và gợi mở từ AI.', icon: <ChatBubbleBottomCenterTextIcon className="h-7 w-7 text-indigo-500" /> },
            { view: View.ScienceExplainer, title: 'Thám hiểm khoa học vui', description: 'Giải mã mọi bí ẩn của vũ trụ và tự nhiên bằng ngôn ngữ dễ hiểu, ví dụ trực quan, kích thích niềm đam mê khám phá trong bạn.', icon: <BeakerIcon className="h-7 w-7 text-cyan-500" /> },
            { view: View.DesignCenter, title: 'Thiết kế thông minh AI', description: 'Tự tay sáng tạo những mẫu thiết kế, học liệu độc đáo và chuyên nghiệp với sự trợ giúp đắc lực từ trí tuệ nhân tạo.', icon: <SparklesIcon className="h-7 w-7 text-violet-500" /> },
            { view: View.Drawing, title: 'Xưởng vẽ sáng tạo AI', description: 'Biến mọi ý tưởng thành tác phẩm nghệ thuật với sự hướng dẫn của AI, giúp bạn tự tin thể hiện tài năng hội họa độc đáo.', icon: <PaintBrushIcon className="h-7 w-7 text-rose-500" /> },
        ]
            .filter((tool) => {
                if (tool.view === View.Drawing) return FEATURE_FLAGS.IMAGE_GENERATION !== false;
                if (tool.view === View.DesignCenter) return FEATURE_FLAGS.IMAGE_GENERATION !== false;
                return true;
            })
            .map((tool) => {
                const cfg = getHomepageToolMediaEntry(map, tool.view);
                return {
                    ...tool,
                    media: cfg.urls !== undefined ? cfg.urls : undefined,
                    title: cfg.title ?? tool.title,
                    description: cfg.description ?? tool.description,
                };
            });
        return orderToolsByConfigKeys(raw, map);
    }, [FEATURE_FLAGS.IMAGE_GENERATION, HOMEPAGE_CONFIG.STUDENT_TOOLS_MEDIA]);

    React.useLayoutEffect(() => {
        scrollTabStripToActiveButton(
            teacherTabStripRef.current,
            teacherTabBtnRefs.current[activeTeacherToolTab] ?? null,
            'smooth',
        );
    }, [activeTeacherToolTab, teacherTools.length]);

    React.useLayoutEffect(() => {
        scrollTabStripToActiveButton(
            studentTabStripRef.current,
            studentTabBtnRefs.current[activeStudentToolTab] ?? null,
            'smooth',
        );
    }, [activeStudentToolTab, studentTools.length]);

    /** Cuộn ngang strip bằng bánh xe (deltaY → scrollLeft); không cuộn cả trang. Thanh scroll ẩn (scrollbar-hide). */
    React.useEffect(() => {
        const node = teacherTabStripRef.current;
        if (!node) return;
        const onWheel = (e: WheelEvent) => {
            if (node.scrollWidth <= node.clientWidth + 1) return;
            const dx = e.deltaX;
            const dy = e.deltaY;
            if (Math.abs(dx) > Math.abs(dy)) return;
            if (dy === 0) return;
            e.preventDefault();
            e.stopPropagation();
            node.scrollLeft += dy;
        };
        node.addEventListener('wheel', onWheel, { passive: false });
        return () => node.removeEventListener('wheel', onWheel);
    }, [teacherTools.length, user?.role]);

    React.useEffect(() => {
        const node = studentTabStripRef.current;
        if (!node) return;
        const onWheel = (e: WheelEvent) => {
            if (node.scrollWidth <= node.clientWidth + 1) return;
            const dx = e.deltaX;
            const dy = e.deltaY;
            if (Math.abs(dx) > Math.abs(dy)) return;
            if (dy === 0) return;
            e.preventDefault();
            e.stopPropagation();
            node.scrollLeft += dy;
        };
        node.addEventListener('wheel', onWheel, { passive: false });
        return () => node.removeEventListener('wheel', onWheel);
    }, [studentTools.length, user?.role]);

    React.useEffect(() => {
        if (user?.role === 'student') {
            onNavigate(View.StudentDashboard);
        }
    }, [user?.role, onNavigate]);

    React.useEffect(() => {
        if (featureShowcaseTools.length === 0) return;
        if (activeFeatureTab >= featureShowcaseTools.length) {
            setActiveFeatureTab(0);
        }
    }, [activeFeatureTab, featureShowcaseTools.length]);

    React.useEffect(() => {
        if (isTeacherSliderPaused) return;
        const len = teacherTools.length;
        if (len === 0) return;
        const timer = setInterval(() => {
            setActiveTeacherToolTab(prev => (prev + 1) % len);
        }, 8000);
        return () => clearInterval(timer);
    }, [teacherTools.length, isTeacherSliderPaused]);

    React.useEffect(() => {
        if (isStudentSliderPaused) return;
        const len = studentTools.length;
        if (len === 0) return;
        const timer = setInterval(() => {
            setActiveStudentToolTab(prev => (prev + 1) % len);
        }, 8000);
        return () => clearInterval(timer);
    }, [studentTools.length, isStudentSliderPaused]);

    if (isLoading && !user) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <LoadingDataIndicator message="Đang khởi tạo hệ thống..." />
            </div>
        );
    }
    
    return (
        <div className="min-w-0 max-w-full space-y-16 pb-12">
            {/* New Modern Hero Section */}
            <section className="relative overflow-hidden bg-white rounded-[40px] shadow-sm border border-slate-100 mt-4">
                <div className="absolute top-0 right-0 -mt-20 -mr-20 w-96 h-96 bg-teal-50 rounded-full blur-3xl opacity-50"></div>
                <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-72 h-72 bg-blue-50 rounded-full blur-3xl opacity-50"></div>
                
                <div className="relative flex flex-col items-center gap-8 p-6 md:p-8 lg:flex-row lg:gap-12 lg:p-10">
                    <div className="flex-1 space-y-8 text-center lg:text-left">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-700 text-[10px] font-black uppercase tracking-[0.2em]">
                            <SparklesIcon className="h-4 w-4" />
                            <span>Trợ lý Giáo dục AI {UI_CONFIG.APP_NAME}</span>
                        </div>
                        <h1 
                            className="text-4xl md:text-5xl lg:text-7xl font-black text-slate-900 leading-[1.1] tracking-tight"
                            dangerouslySetInnerHTML={{ __html: HOMEPAGE_CONFIG.HERO_TITLE ?? '' }}
                        />
                        <p 
                            className="text-lg md:text-xl text-slate-500 max-w-2xl mx-auto lg:mx-0 leading-relaxed font-medium"
                            dangerouslySetInnerHTML={{ __html: HOMEPAGE_CONFIG.HERO_SUBTITLE || '' }}
                        />
                        <div className="flex flex-wrap justify-center lg:justify-start gap-4 pt-4">
                            <button 
                                onClick={handleStartNow}
                                className="px-10 py-5 bg-teal-600 text-white font-black rounded-2xl shadow-2xl shadow-teal-600/20 hover:bg-teal-700 transition-all transform hover:scale-105 active:scale-95 flex items-center gap-3 text-lg"
                            >
                                <DocumentTextIcon className="h-6 w-6" />
                                <span>Bắt đầu ngay</span>
                            </button>
                            <button 
                                onClick={() => document.getElementById('feature-showcase')?.scrollIntoView({ behavior: 'smooth' })}
                                className="px-10 py-5 bg-white text-slate-700 font-black rounded-2xl border border-slate-200 hover:border-teal-200 hover:bg-slate-50 transition-all flex items-center gap-3 text-lg shadow-sm"
                            >
                                <PlayIcon className="h-6 w-6 text-teal-600" />
                                <span>Xem tính năng</span>
                            </button>
                        </div>
                    </div>
                    
                    <div className="flex-1 relative w-full max-w-2xl">
                        {/* Ảnh cấu hình hoặc mockup AI Dashboard mặc định */}
                        <div className="relative z-10 p-2 md:p-4 bg-slate-50/50 backdrop-blur-sm rounded-[40px] border border-white/50 shadow-2xl">
                            {useHeroMockupImage ? (
                                <div className="rounded-[32px] overflow-hidden">
                                    <img
                                        src={resolvePublicFileUrl(heroMockupImage)}
                                        alt=""
                                        className="block w-full min-h-[400px] object-cover object-center"
                                        loading="lazy"
                                    />
                                </div>
                            ) : (
                            <MockupWindow title="AI Dashboard" className="bg-white min-h-[400px]">
                                <div className="h-full flex flex-col">
                                    <div className="bg-slate-50/50 border-b border-slate-100 p-4 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="h-8 w-8 rounded-lg bg-teal-600 shadow-lg shadow-teal-600/20" />
                                            <div className="h-3 w-32 bg-slate-200 rounded-full" />
                                        </div>
                                        <div className="flex gap-2">
                                            <div className="h-8 w-8 rounded-full bg-white border border-slate-100 flex items-center justify-center shadow-sm">
                                                <div className="h-4 w-4 rounded-full bg-slate-200" />
                                            </div>
                                            <div className="h-8 w-8 rounded-full bg-white border border-slate-100 flex items-center justify-center shadow-sm">
                                                <div className="h-4 w-4 rounded-full bg-slate-200" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="p-8 grid grid-cols-2 gap-6">
                                        <div className="p-5 rounded-2xl bg-white border border-slate-100 space-y-4 shadow-sm group hover:border-blue-200 transition-colors">
                                            <div className="h-12 w-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center"><DocumentTextIcon className="h-6 w-6" /></div>
                                            <div className="space-y-2">
                                                <div className="h-3 w-full bg-slate-100 rounded-full" />
                                                <div className="h-2 w-2/3 bg-slate-50 rounded-full" />
                                            </div>
                                        </div>
                                        <div className="p-5 rounded-2xl bg-white border border-slate-100 space-y-4 shadow-sm group hover:border-green-200 transition-colors">
                                            <div className="h-12 w-12 rounded-xl bg-green-50 text-green-600 flex items-center justify-center"><PresentationChartBarIcon className="h-6 w-6" /></div>
                                            <div className="space-y-2">
                                                <div className="h-3 w-full bg-slate-100 rounded-full" />
                                                <div className="h-2 w-2/3 bg-slate-50 rounded-full" />
                                            </div>
                                        </div>
                                        <div className="p-5 rounded-2xl bg-white border border-slate-100 space-y-4 shadow-sm group hover:border-purple-200 transition-colors">
                                            <div className="h-12 w-12 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center"><QuestionMarkCircleIcon className="h-6 w-6" /></div>
                                            <div className="space-y-2">
                                                <div className="h-3 w-full bg-slate-100 rounded-full" />
                                                <div className="h-2 w-2/3 bg-slate-50 rounded-full" />
                                            </div>
                                        </div>
                                        <div className="p-5 rounded-2xl bg-teal-600 text-white space-y-4 shadow-xl shadow-teal-600/30">
                                            <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center backdrop-blur-md"><SparklesIcon className="h-6 w-6" /></div>
                                            <div className="text-xs font-black uppercase tracking-widest">AI Agent</div>
                                            <div className="h-2 w-full bg-white/20 rounded-full" />
                                        </div>
                                    </div>
                                </div>
                            </MockupWindow>
                            )}
                            
                            {/* Floating Video Overlay */}
                            {hasHomepageVideo && (
                            <div 
                                onClick={() => setIsVideoModalOpen(true)}
                                className="absolute -bottom-8 -left-8 md:-left-12 z-20 w-48 md:w-64 aspect-video bg-gradient-to-br from-teal-500 to-blue-600 rounded-2xl shadow-2xl border-4 border-white overflow-hidden flex items-center justify-center group cursor-pointer transform hover:-rotate-3 transition-transform"
                            >
                                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                    <div className="w-12 h-12 bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center text-white scale-90 group-hover:scale-100 transition-transform">
                                        <PlayIcon className="h-6 w-6 ml-1" />
                                    </div>
                                </div>
                                <div className="text-white text-center p-4">
                                    <PlayIcon className="h-10 w-10 mx-auto mb-2 opacity-80" />
                                    <p className="text-xs font-black uppercase tracking-[0.2em]">Xem Video</p>
                                </div>
                            </div>
                            )}
                            
                            {/* Stats Badge — đặt trong khung (tránh bị cắt bởi overflow) */}
                            <div className="absolute top-3 right-3 md:top-4 md:right-4 z-20 max-w-[min(100%,11rem)] p-4 md:p-5 bg-white rounded-2xl md:rounded-3xl shadow-2xl border border-slate-100 animate-bounce-slow">
                                <div className="text-2xl md:text-3xl font-black text-teal-600 leading-none">{HOMEPAGE_CONFIG.HERO_BADGE_VALUE || '500k+'}</div>
                                <div className="text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1.5 md:mt-2 leading-snug">{HOMEPAGE_CONFIG.HERO_BADGE_LABEL || 'Học liệu đã tạo'}</div>
                            </div>
                        </div>
                        
                        {/* Decorative blobs */}
                        <div className="absolute -top-10 -right-10 w-64 h-64 bg-teal-200/30 rounded-full blur-[80px]"></div>
                        <div className="absolute -bottom-10 -left-10 w-64 h-64 bg-blue-200/30 rounded-full blur-[80px]"></div>
                    </div>
                </div>
            </section>

            {/* Video Modal */}
            {isVideoModalOpen && hasHomepageVideo && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4" onClick={() => setIsVideoModalOpen(false)}>
                    <div className="relative w-full max-w-5xl aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
                        <button 
                            onClick={() => setIsVideoModalOpen(false)}
                            className="absolute top-6 right-6 z-10 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors border border-white/10"
                        >
                            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                        {HOMEPAGE_CONFIG.VIDEO_SOURCE === 'server' && HOMEPAGE_CONFIG.VIDEO_URL ? (
                            <video
                                className="w-full h-full object-contain"
                                src={HOMEPAGE_CONFIG.VIDEO_URL.startsWith('http') ? HOMEPAGE_CONFIG.VIDEO_URL : resolvePublicFileUrl(HOMEPAGE_CONFIG.VIDEO_URL)}
                                controls
                                autoPlay
                                title={`Giới thiệu ${UI_CONFIG.APP_NAME}`}
                            />
                        ) : (
                            <iframe 
                                className="w-full h-full"
                                src={`https://www.youtube.com/embed/${homepageYoutubeId}?autoplay=1&rel=0&modestbranding=1`}
                                title={`Giới thiệu ${UI_CONFIG.APP_NAME}`}
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            ></iframe>
                        )}
                    </div>
                </div>
            )}

            {/* Feature Showcase Section */}
            <section id="feature-showcase" className="scroll-mt-20">
                {(() => {
                    const tool = activeFeatureShowcaseTool;
                    if (!tool) return null;
                    const hasMedia = tool?.media && tool.media.length > 0;
                    const fallbackShowcaseImg = tool.fallbackImage || FEATURE_SHOWCASE_FALLBACK_IMAGES[activeFeatureTab % FEATURE_SHOWCASE_FALLBACK_IMAGES.length];

                    return (
                        <div className="relative flex min-h-[min(92vh,820px)] items-center overflow-hidden rounded-[60px] bg-slate-950 p-6 text-white shadow-2xl md:p-8 lg:p-10">
                            {/* Full-bleed visual: phải rõ, trái tan vào nền chữ — không khung ảnh */}
                            <div className="absolute inset-0 z-0">
                                {hasMedia ? (
                                    <MediaCarousel
                                        media={tool.media}
                                        bleed
                                        className="absolute inset-0 h-full min-h-[min(92vh,820px)] w-full"
                                        overlay={
                                            <>
                                                <div
                                                    className="absolute inset-y-0 left-0 w-[78%] bg-gradient-to-r from-slate-950 from-[0%] via-slate-950/92 via-[55%] to-transparent to-[100%]"
                                                    aria-hidden
                                                />
                                                <div
                                                    className="absolute bottom-0 left-0 h-[38%] w-[72%] bg-gradient-to-t from-slate-950/65 via-slate-950/15 to-transparent"
                                                    aria-hidden
                                                />
                                                <div className="absolute left-0 right-[22%] top-0 h-28 bg-gradient-to-b from-slate-950/40 to-transparent" aria-hidden />
                                            </>
                                        }
                                    />
                                ) : (
                                    <>
                                        <img
                                            src={fallbackShowcaseImg}
                                            alt=""
                                            className="absolute inset-0 h-full w-full object-cover object-right scale-[1.02]"
                                            loading="lazy"
                                        />
                                        <div
                                            className="pointer-events-none absolute inset-y-0 left-0 z-[1] w-[78%] bg-gradient-to-r from-slate-950 from-[0%] via-slate-950/92 via-[55%] to-transparent to-[100%]"
                                            aria-hidden
                                        />
                                        <div
                                            className="pointer-events-none absolute bottom-0 left-0 z-[1] h-[38%] w-[72%] bg-gradient-to-t from-slate-950/65 via-slate-950/15 to-transparent"
                                            aria-hidden
                                        />
                                        <div
                                            className="pointer-events-none absolute left-0 right-[22%] top-0 z-[1] h-28 bg-gradient-to-b from-slate-950/40 to-transparent"
                                            aria-hidden
                                        />
                                        <div className="absolute top-0 right-0 z-[1] h-[640px] w-[min(100%,640px)] rounded-full bg-teal-500/15 blur-[100px] pointer-events-none -translate-y-1/4 translate-x-1/4" />
                                        <div className="absolute bottom-0 left-1/2 z-[1] h-[520px] w-[520px] translate-y-1/3 rounded-full bg-blue-500/10 blur-[90px] pointer-events-none" />
                                    </>
                                )}
                            </div>

                            <div className="relative z-20 w-full grid lg:grid-cols-[minmax(0,34rem),1fr] gap-10 lg:gap-14 xl:gap-20 items-center">
                                {/* Content Block with Glassmorphism */}
                                <div className="space-y-8 rounded-[32px] border border-white/15 bg-slate-950/30 p-6 shadow-2xl backdrop-blur-md md:p-8">
                                    <div className="space-y-4">
                                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-teal-400 text-[10px] font-black uppercase tracking-[0.2em] border border-white/10">
                                            <SparklesIcon className="h-4 w-4" />
                                            <span>Công cụ nổi bật</span>
                                        </div>
                                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] tracking-tight drop-shadow-[0_2px_24px_rgba(0,0,0,0.65)]">Đổi mới <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-300 to-blue-300 drop-shadow-sm">cách dạy và học</span></h2>
                                        <p className="text-lg text-slate-200/95 font-medium max-w-lg leading-relaxed drop-shadow-md">Biến những ý tưởng giảng dạy thành học liệu trực quan, dễ dùng và phù hợp hơn với lớp học mỗi ngày.</p>
                                    </div>
                                    
                                    <div className="space-y-3">
                                        {featureShowcaseTools.map((item: any, i: number) => (
                                            <div 
                                                key={`${item.view}-${i}`}
                                                onClick={() => setActiveFeatureTab(i)}
                                                className={`p-5 rounded-2xl border transition-all cursor-pointer flex items-center gap-5 group/item ${activeFeatureTab === i ? 'bg-white/15 border-white/20 shadow-xl scale-[1.02]' : 'bg-transparent border-white/5 hover:bg-white/5 opacity-60 hover:opacity-100'}`}
                                            >
                                                <div className={`h-11 w-11 rounded-xl flex items-center justify-center shadow-lg transition-transform ${activeFeatureTab === i ? 'bg-teal-500 text-white scale-110' : 'bg-white/10 text-white/60'}`}>
                                                    {getIcon(item.icon, "h-5 w-5")}
                                                </div>
                                                <div className="flex-1">
                                                    <div className={`font-black text-lg tracking-tight transition-colors ${activeFeatureTab === i ? 'text-white' : 'text-slate-300'}`}>{item.title}</div>
                                                    {activeFeatureTab === i && <p className="text-xs text-teal-400/90 font-bold mt-0.5 line-clamp-1">{item.description || 'Khám phá sức mạnh sáng tạo không giới hạn'}</p>}
                                                </div>
                                                {activeFeatureTab === i && <ChevronRightIcon className="h-5 w-5 text-teal-400 animate-pulse" />}
                                            </div>
                                        ))}
                                    </div>
                                    
                                    <button onClick={handleFeatureShowcaseStart} className="w-full sm:w-auto px-10 py-5 bg-white text-slate-900 font-black rounded-2xl hover:bg-teal-50 transition-all flex items-center justify-center gap-3 shadow-2xl group active:scale-95">
                                        <span>Bắt đầu dùng ngay</span>
                                        <ChevronRightIcon className="h-6 w-6 group-hover:translate-x-1 transition-transform" />
                                    </button>
                                </div>

                                {/* Cột phải: có media thì để trống (ảnh đã full nền); không media thì mockup UI nổi — không lồng khung kính */}
                                <div className={`relative min-h-[280px] lg:min-h-[420px] ${hasMedia ? 'hidden lg:block' : ''}`}>
                                    {hasMedia ? (
                                        <div className="pointer-events-none absolute bottom-6 right-0 z-30 flex items-center gap-2 rounded-full border border-white/15 bg-slate-950/50 px-4 py-2 text-[10px] font-black uppercase tracking-[0.2em] text-white/80 backdrop-blur-md">
                                            <SparklesIcon className="h-4 w-4 text-amber-300" />
                                            AI Studio
                                        </div>
                                    ) : (
                                        <div className="relative z-10 ml-auto w-full max-w-xl lg:max-w-[440px]">
                                            <div className="overflow-hidden rounded-2xl border border-white/20 bg-slate-950/45 shadow-[0_24px_80px_rgba(0,0,0,0.45)] backdrop-blur-md">
                                                {renderFeatureShowcaseMockup(tool.view)}
                                            </div>
                                            <div className="pointer-events-none absolute -bottom-2 -right-2 z-20 rounded-2xl border border-white/30 bg-gradient-to-br from-amber-400/95 to-orange-500/95 px-3 py-2 shadow-lg">
                                                <SparklesIcon className="mx-auto h-4 w-4 text-white" />
                                                <span className="mt-0.5 block text-center text-[8px] font-black uppercase tracking-widest text-white">AI</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })()}
            </section>

            {/* How It Works — đặt sau khối tính năng nổi bật (feature showcase) */}
            <section className="py-12">
                <div className="text-center space-y-4 mb-16">
                    <div className="inline-flex px-4 py-1.5 rounded-full bg-slate-100 text-slate-500 text-[10px] font-black uppercase tracking-[0.2em]">Quy trình 3 bước</div>
                    <h2 className="text-4xl font-black text-slate-900 tracking-tight">Sử dụng AI thật dễ dàng</h2>
                    <p className="text-slate-500 max-w-xl mx-auto font-medium">Nâng tầm giảng dạy không còn là thử thách. Mọi thứ đều được tự động hóa thông minh giúp bạn tiết kiệm hàng giờ mỗi ngày.</p>
                </div>
                <div className="grid md:grid-cols-3 gap-10">
                    {[
                        { step: '01', title: 'Nhập chủ đề', desc: 'Chỉ cần nhập tên bài học hoặc tải lên tài liệu sẵn có. AI sẽ hiểu nội dung của bạn.', icon: <ChatBubbleLeftRightIcon className="h-7 w-7" />, color: 'bg-blue-600 shadow-blue-600/20' },
                        { step: '02', title: 'AI Sáng tạo', desc: 'AI tự động thiết kế giáo án, slide, quiz và mô phỏng 3D chỉ trong vài giây.', icon: <SparklesIcon className="h-7 w-7" />, color: 'bg-teal-600 shadow-teal-600/20' },
                        { step: '03', title: 'Tùy chỉnh & Dạy', desc: 'Chỉnh sửa nhanh theo ý muốn và xuất bản bài giảng chuyên nghiệp tới học sinh.', icon: <TrophyIcon className="h-7 w-7" />, color: 'bg-amber-500 shadow-amber-500/20' }
                    ].map((item, i) => (
                        <div key={i} className="group relative rounded-[32px] border border-slate-100 bg-white p-6 shadow-sm transition-all hover:-translate-y-2 hover:shadow-xl md:p-8">
                            <div className="absolute top-6 right-8 text-5xl font-black text-slate-50 group-hover:text-slate-100 transition-colors tracking-tighter italic">{item.step}</div>
                            <div className={`mb-8 h-16 w-16 rounded-2xl flex items-center justify-center text-white shadow-2xl relative z-10 ${item.color}`}>
                                {item.icon}
                            </div>
                            <h3 className="text-2xl font-black text-slate-800 mb-3">{item.title}</h3>
                            <p className="text-slate-500 leading-relaxed font-medium">{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                {(HOMEPAGE_CONFIG.STATS || []).map((stat: { label: string; value: string; color: string; bg: string }, i: number) => (
                    <div key={i} className={`rounded-[40px] border border-white/50 p-6 text-center shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl md:p-8 ${stat.bg}`}>
                        <p className={`text-5xl font-black ${stat.color} tracking-tighter`}>{stat.value}</p>
                        <p className="text-xs font-black text-slate-500 uppercase tracking-[0.3em] mt-3">{stat.label}</p>
                    </div>
                ))}
            </div>

            {/* Role Sections */}
            <div className="space-y-20 pt-8">
                {user?.role !== 'student' && (
                <section id="teacher-section">
                    <div className="text-center space-y-4 mb-12">
                        <div className="inline-flex px-4 py-1.5 rounded-full bg-teal-50 text-teal-700 text-[10px] font-black uppercase tracking-[0.2em] border border-teal-100 shadow-sm">
                            <AcademicCapIcon className="h-4 w-4 mr-2" />
                            Dành cho Giáo viên
                        </div>
                        <h2 className="text-4xl md:text-6xl font-black text-slate-900 tracking-tight" dangerouslySetInnerHTML={{ __html: HOMEPAGE_CONFIG.TEACHER_SECTION_TITLE || '' }} />
                        <p className="text-slate-500 text-xl font-medium italic" dangerouslySetInnerHTML={{ __html: HOMEPAGE_CONFIG.TEACHER_SECTION_DESCRIPTION || 'Nâng cao hiệu suất giảng dạy với trợ lý AI' }} />
                    </div>

                    <div 
                        onMouseEnter={() => setIsTeacherSliderPaused(true)}
                        onMouseLeave={() => setIsTeacherSliderPaused(false)}
                        className="relative bg-slate-950 rounded-[60px] overflow-hidden shadow-2xl min-h-[600px] flex flex-col border border-slate-800"
                    >
                        {/* Interactive Slide Viewer */}
                        <div className="flex-1 relative overflow-hidden">
                            {teacherTools.map((tool, idx) => (
                                <div 
                                    key={tool.view}
                                    className={`absolute inset-0 transition-all duration-1000 ease-in-out flex items-center ${activeTeacherToolTab === idx ? 'opacity-100 translate-x-0 z-10' : (activeTeacherToolTab > idx ? 'opacity-0 -translate-x-full z-0' : 'opacity-0 translate-x-full z-0')}`}
                                >
                                    {/* Full-bleed Background Media */}
                                    <div className="absolute inset-0 z-0 overflow-hidden">
                                        {tool.media && tool.media.length > 0 ? (
                                            <MediaCarousel media={tool.media} className="h-full rounded-none opacity-60 scale-105" />
                                        ) : (
                                            <div className="w-full h-full bg-slate-900 flex items-center justify-center opacity-30">
                                                {tool.mockup || <div className="text-slate-600 scale-[2]">{tool.icon}</div>}
                                            </div>
                                        )}
                                        {/* Dynamic Gradient Overlays */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/60 to-transparent z-10" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/20 z-10" />
                                    </div>

                                    {/* Main Content Overlay with Animation */}
                                    <div className={`relative z-20 grid w-full grid-cols-1 items-center gap-8 px-6 py-10 transition-all delay-300 duration-1000 md:px-8 lg:grid-cols-[1.2fr,1fr] lg:gap-10 lg:px-8 ${activeTeacherToolTab === idx ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                                        <div className="space-y-8">
                                            <div className="inline-flex items-center gap-4 px-5 py-2.5 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 text-white shadow-2xl">
                                                <div className="h-10 w-10 bg-teal-500 text-white rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/30">
                                                    {tool.icon}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-black uppercase tracking-widest text-[10px] text-teal-400">Công cụ chuyên sâu</span>
                                                    <span className="font-black text-xl tracking-tight leading-none">{tool.title}</span>
                                                </div>
                                            </div>
                                            
                                            <h3 className="text-4xl md:text-5xl font-black text-white leading-[1.15] tracking-tight drop-shadow-2xl">
                                                {tool.title.includes('Mới') ? tool.title : `${tool.title}`}
                                            </h3>
                                            
                                            <p className="text-xl font-medium text-slate-300 leading-relaxed max-w-xl drop-shadow-lg">
                                                {tool.description}
                                            </p>
                                            
                                            <div className="flex flex-wrap gap-4 pt-4">
                                                <button 
                                                    onClick={() => navigateWithLoginRedirect(tool.view)}
                                                    className="px-10 py-5 bg-teal-600 text-white font-black rounded-2xl shadow-2xl shadow-teal-600/40 hover:bg-teal-700 transition-all transform hover:scale-105 active:scale-95 flex items-center gap-3 text-lg"
                                                >
                                                    <SparklesIcon className="h-6 w-6" />
                                                    <span>Trải nghiệm ngay</span>
                                                </button>
                                                
                                                <div className="flex -space-x-3 items-center">
                                                    {teacherSocialAvatars.map((src, i) => (
                                                        <div key={i} className="h-10 w-10 rounded-full border-2 border-slate-900 bg-slate-800 flex items-center justify-center overflow-hidden">
                                                            <img src={src} alt="" className="w-full h-full object-cover grayscale" />
                                                        </div>
                                                    ))}
                                                    <div className="pl-6 text-sm text-slate-400 font-bold uppercase tracking-widest">{teacherSocialCaption}</div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Ảnh minh họa tràn cạnh, mờ về phía chữ — tránh “ảnh trong hộp kính” */}
                                        <div className="relative hidden min-h-[400px] overflow-hidden rounded-2xl ring-1 ring-white/10 lg:block">
                                            <div className="absolute -inset-px bg-gradient-to-br from-teal-500/15 to-transparent opacity-80" />
                                            {tool.media && tool.media.length > 0 ? (
                                                <>
                                                    <img
                                                        src={resolvePublicFileUrl(tool.media[0])}
                                                        className="h-[min(420px,52vh)] w-full object-cover object-right"
                                                        alt={tool.title}
                                                    />
                                                    <div
                                                        className="pointer-events-none absolute inset-0 bg-gradient-to-l from-transparent via-slate-950/25 to-slate-950/70"
                                                        aria-hidden
                                                    />
                                                    <div className="pointer-events-none absolute bottom-5 right-5 rounded-xl border border-white/20 bg-slate-950/60 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-white/90 backdrop-blur-sm">
                                                        AI Studio
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="flex h-[min(420px,52vh)] w-full items-center justify-center bg-slate-900/80">
                                                    {tool.mockup || <SparklesIcon className="h-20 w-20 text-slate-700" />}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Modern Tabbed Navigation (Bottom Bar) */}
                        <div className="relative z-30 shrink-0 min-w-0 bg-white/5 backdrop-blur-[32px] border-t border-white/10 p-4">
                            <div
                                ref={teacherTabStripRef}
                                className="homepage-tab-strip flex min-w-0 gap-4 overflow-x-auto overflow-y-hidden scroll-smooth overscroll-contain px-4 py-2 sm:px-6 max-w-7xl mx-auto items-center touch-pan-x scrollbar-hide"
                            >
                                {teacherTools.map((tool, idx) => (
                                    <button
                                        key={tool.view}
                                        type="button"
                                        ref={(el) => {
                                            teacherTabBtnRefs.current[idx] = el;
                                        }}
                                        onClick={() => setActiveTeacherToolTab(idx)}
                                        className={`px-6 py-4 rounded-2xl transition-all flex items-center gap-4 shrink-0 border-2 ${
                                            activeTeacherToolTab === idx 
                                                ? 'bg-white border-white text-slate-900 shadow-[0_10px_30px_rgba(255,255,255,0.2)] scale-110 z-10' 
                                                : 'bg-white/5 border-white/10 text-white/40 hover:bg-white/10 hover:text-white/80 hover:border-white/20'
                                        }`}
                                    >
                                        <div className={`h-8 w-8 flex items-center justify-center rounded-xl transition-all ${activeTeacherToolTab === idx ? 'bg-teal-50 text-teal-600' : 'bg-white/10 text-white/40'}`}>
                                            {React.cloneElement<{ className?: string }>(tool.icon as React.ReactElement<{ className?: string }>, { className: 'h-5 w-5' })}
                                        </div>
                                        <div className="flex flex-col items-start">
                                            <span className="font-black text-sm whitespace-nowrap tracking-tight leading-none">{tool.title}</span>
                                            {activeTeacherToolTab === idx && <div className="h-1 w-8 bg-teal-500 rounded-full mt-1.5 animate-grow-x" />}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
                )}

                {/* Lộ trình HS & PH — đặt sau khối Giáo viên (với tài khoản học sinh: sau thống kê, trước Góc học tập) */}
                <section id="personal-learning-plan-promo" className="relative py-6">
                    <div className="relative overflow-hidden rounded-[48px] border border-teal-100/50 shadow-md">
                        {/* Nền chung cả section: gradient + ảnh mờ đan xen, không tách trắng / xanh đậm hai nửa */}
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-teal-50/95 via-white to-sky-50/70" aria-hidden />
                        <div
                            className="pointer-events-none absolute inset-0 bg-cover bg-[position:72%_42%] opacity-[0.2] mix-blend-multiply"
                            style={{
                                backgroundImage:
                                    'url(/assets/images/dashboard/personal-learning/promo-bg.webp)',
                            }}
                            aria-hidden
                        />
                        <div
                            className="pointer-events-none absolute inset-0 bg-gradient-to-r from-white/95 from-[0%] via-teal-50/55 via-[42%] to-sky-100/35 to-[100%]"
                            aria-hidden
                        />
                        <div
                            className="pointer-events-none absolute inset-y-0 right-0 w-[56%] max-w-2xl bg-gradient-to-l from-teal-100/20 via-teal-50/10 to-transparent"
                            aria-hidden
                        />

                        <div className="relative z-10 grid items-center gap-8 p-6 md:p-8 lg:min-h-[min(600px,78vh)] lg:grid-cols-[minmax(0,1.12fr)_minmax(0,0.88fr)] lg:gap-10">
                            <div className="space-y-6">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-600 text-white shadow-lg shadow-teal-600/25">
                                        <TrophyIcon className="h-6 w-6" />
                                    </div>
                                    <div className="inline-flex rounded-full border border-teal-100/80 bg-teal-50/90 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-teal-800">
                                        Học sinh & Phụ huynh
                                    </div>
                                </div>
                                <h2 className="text-3xl font-black leading-[1.12] tracking-tight text-slate-900 sm:text-4xl md:text-5xl">
                                    Lộ trình học tập <br />
                                    <span className="bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent">cá nhân hóa bởi AI</span>
                                </h2>
                                <p className="max-w-xl text-base font-medium leading-relaxed text-slate-600 md:text-lg">
                                    AI không chỉ dành cho giáo viên. Chúng tôi xây dựng một người bạn đồng hành thông minh giúp học sinh học tập chủ động và phụ huynh dễ dàng theo sát tiến độ.
                                </p>

                                <ul className="space-y-4">
                                    {[
                                        'Thiết lập mục tiêu linh hoạt, ngắn hạn & dài hạn.',
                                        'Nội dung cá nhân hóa theo độ tuổi và trình độ.',
                                        'Động viên và nhắc nhở 24/7 từ Trợ lý AI.',
                                    ].map((item, i) => (
                                        <li key={i} className="group flex items-start gap-4 text-base font-bold text-slate-700 md:text-lg">
                                            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-emerald-100 bg-emerald-50 text-emerald-600 transition-colors group-hover:bg-emerald-500 group-hover:text-white">
                                                <CheckIcon className="h-4 w-4" />
                                            </div>
                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>

                                <div className="pt-1">
                                    <button
                                        onClick={handleLearningPlanStart}
                                        className="flex items-center gap-3 rounded-2xl bg-teal-600 px-8 py-4 text-base font-black text-white shadow-lg shadow-teal-600/20 transition-transform hover:bg-teal-700 active:scale-95 md:text-lg"
                                    >
                                        <span>Khám phá Lộ trình học tập</span>
                                        <ChevronRightIcon className="h-6 w-6" />
                                    </button>
                                    {!user && (
                                        <p className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-400">Đăng nhập để nhận lộ trình miễn phí</p>
                                    )}
                                </div>
                            </div>

                            {/* Cột map: cùng nền với trái — chỉ thêm lớp kính rất nhẹ để đọc SVG */}
                            <div className="relative flex min-h-[min(400px,55vh)] flex-col rounded-2xl border border-teal-100/25 bg-white/20 p-4 shadow-inner backdrop-blur-[2px] md:min-h-[min(440px,58vh)] lg:min-h-[min(480px,62vh)]">
                                <div className="mb-4 flex flex-shrink-0 flex-wrap items-center justify-between gap-2">
                                    <div className="text-xl font-black italic tracking-tight text-slate-800 md:text-2xl">Học tập 4.0</div>
                                    <div className="rounded-full border border-teal-200/60 bg-teal-50/90 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-teal-800">
                                        AI Managed
                                    </div>
                                </div>

                                <div className="relative min-h-[min(300px,42vh)] w-full flex-1 lg:min-h-0">
                                        <svg
                                            className="absolute inset-0 h-full w-full"
                                            viewBox="0 0 420 440"
                                            preserveAspectRatio="xMidYMid meet"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                            aria-label="Lộ trình học tập 4 bước — từ dưới lên, trái sang phải"
                                        >
                                            <defs>
                                                <linearGradient id="roadmap-line" x1="0%" y1="100%" x2="100%" y2="0%">
                                                    <stop offset="0%" stopColor="rgb(13 148 136)" stopOpacity="1" />
                                                    <stop offset="100%" stopColor="rgb(37 99 235)" stopOpacity="0.92" />
                                                </linearGradient>
                                                <filter id="roadmap-node-shadow" x="-30%" y="-30%" width="160%" height="160%">
                                                    <feDropShadow dx="0" dy="2" stdDeviation="4" floodOpacity="0.22" />
                                                </filter>
                                                <filter id="roadmap-label-glow" x="-20%" y="-20%" width="140%" height="140%">
                                                    <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodColor="#ffffff" floodOpacity="0.95" />
                                                </filter>
                                            </defs>
                                            {/* Chỉ đường + mốc — phong cảnh từ ảnh nền, không khung fill trong SVG */}
                                            <path
                                                d="M 44 382 C 92 348 118 318 148 278 C 182 232 215 198 252 162 C 288 128 318 92 358 52"
                                                stroke="url(#roadmap-line)"
                                                strokeWidth="4"
                                                strokeLinecap="round"
                                                strokeDasharray="12 14"
                                                fill="none"
                                                opacity={0.95}
                                            />
                                            {[
                                                {
                                                    x: 44,
                                                    y: 382,
                                                    tx: 8,
                                                    ty: 418,
                                                    anchor: 'start' as const,
                                                    label: 'Xác định mục tiêu',
                                                    sub: 'Hoàn thành',
                                                    done: true,
                                                },
                                                {
                                                    x: 148,
                                                    y: 278,
                                                    tx: 158,
                                                    ty: 268,
                                                    anchor: 'start' as const,
                                                    label: 'Nội dung bài học',
                                                    sub: 'Hoàn thành',
                                                    done: true,
                                                },
                                                {
                                                    x: 252,
                                                    y: 162,
                                                    tx: 262,
                                                    ty: 152,
                                                    anchor: 'start' as const,
                                                    label: 'Luyện tập chuyên sâu',
                                                    sub: 'Hoàn thành',
                                                    done: true,
                                                },
                                                {
                                                    x: 358,
                                                    y: 52,
                                                    tx: 328,
                                                    ty: 28,
                                                    anchor: 'end' as const,
                                                    label: 'Kiểm tra & Đánh giá',
                                                    sub: 'Đang mở',
                                                    done: false,
                                                },
                                            ].map((node, i) => (
                                                <g key={node.label}>
                                                    <circle
                                                        cx={node.x}
                                                        cy={node.y}
                                                        r={node.done ? 17 : 15}
                                                        fill={node.done ? '#14b8a6' : '#ffffff'}
                                                        stroke={node.done ? '#ffffff' : '#e2e8f0'}
                                                        strokeWidth="3"
                                                        filter="url(#roadmap-node-shadow)"
                                                    />
                                                    {node.done ? (
                                                        <path
                                                            d={`M ${node.x - 6} ${node.y} L ${node.x - 2} ${node.y + 5} L ${node.x + 7} ${node.y - 6}`}
                                                            stroke="white"
                                                            strokeWidth="2.2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            fill="none"
                                                        />
                                                    ) : (
                                                        <text
                                                            x={node.x}
                                                            y={node.y + 5}
                                                            textAnchor="middle"
                                                            fill="#94a3b8"
                                                            fontSize="12"
                                                            fontWeight="800"
                                                        >
                                                            {i + 1}
                                                        </text>
                                                    )}
                                                    <text
                                                        x={node.tx}
                                                        y={node.ty}
                                                        textAnchor={node.anchor}
                                                        fill={node.done ? '#0f172a' : '#64748b'}
                                                        fontSize="11"
                                                        fontWeight="800"
                                                        letterSpacing="0.06em"
                                                        filter="url(#roadmap-label-glow)"
                                                        style={{ textTransform: 'uppercase' }}
                                                    >
                                                        {node.label}
                                                    </text>
                                                    <text
                                                        x={node.tx}
                                                        y={node.ty + 14}
                                                        textAnchor={node.anchor}
                                                        fill="#64748b"
                                                        fontSize="9"
                                                        fontWeight="700"
                                                        letterSpacing="0.12em"
                                                        filter="url(#roadmap-label-glow)"
                                                        style={{ textTransform: 'uppercase' }}
                                                    >
                                                        {node.sub}
                                                    </text>
                                                </g>
                                            ))}
                                        </svg>
                                    </div>

                                <div className="pointer-events-none absolute bottom-3 right-3 z-20 rounded-xl border border-amber-200/60 bg-amber-400/90 p-3 shadow-md md:bottom-5 md:right-5">
                                    <TrophyIcon className="h-8 w-8 text-amber-950" />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section id="student-parent-section">
                    <div className="text-center space-y-4 mb-12">
                        <div className="inline-flex px-4 py-1.5 rounded-full bg-violet-50 text-violet-800 text-[10px] font-black uppercase tracking-[0.2em] border border-violet-200/80 shadow-sm">
                            <BookOpenIcon className="h-4 w-4 mr-2 text-violet-600" />
                            Góc học tập
                        </div>
                        <h2 className="text-4xl md:text-6xl font-black text-slate-900 tracking-tight" dangerouslySetInnerHTML={{ __html: HOMEPAGE_CONFIG.STUDENT_SECTION_TITLE || '' }} />
                        <p className="text-violet-700/80 text-xl font-medium italic" dangerouslySetInnerHTML={{ __html: HOMEPAGE_CONFIG.STUDENT_SECTION_DESCRIPTION || 'Học cá nhân hóa với trợ lý AI' }} />
                    </div>

                    <div
                        onMouseEnter={() => setIsStudentSliderPaused(true)}
                        onMouseLeave={() => setIsStudentSliderPaused(false)}
                        className="relative rounded-[60px] overflow-hidden shadow-2xl min-h-[600px] flex flex-col border border-violet-500/25 bg-gradient-to-br from-indigo-950 via-slate-950 to-violet-950 shadow-violet-950/20"
                    >
                        <div className="flex-1 relative overflow-hidden">
                            {studentTools.map((tool, idx) => (
                                <div
                                    key={tool.view}
                                    className={`absolute inset-0 transition-all duration-1000 ease-in-out flex items-center ${activeStudentToolTab === idx ? 'opacity-100 translate-x-0 z-10' : (activeStudentToolTab > idx ? 'opacity-0 -translate-x-full z-0' : 'opacity-0 translate-x-full z-0')}`}
                                >
                                    <div className="absolute inset-0 z-0 overflow-hidden">
                                        {tool.media && tool.media.length > 0 ? (
                                            <MediaCarousel media={tool.media} className="h-full rounded-none opacity-60 scale-105" />
                                        ) : (
                                            <div className="w-full h-full bg-indigo-950/80 flex items-center justify-center opacity-40">
                                                <div className="text-violet-300/90 scale-[2]">{tool.icon}</div>
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-r from-indigo-950 via-indigo-950/55 to-transparent z-10" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-violet-950/90 via-transparent to-indigo-950/30 z-10" />
                                    </div>

                                    <div className={`relative z-20 grid w-full grid-cols-1 items-center gap-8 px-6 py-10 transition-all delay-300 duration-1000 md:px-8 lg:grid-cols-[1.2fr,1fr] lg:gap-10 lg:px-8 ${activeStudentToolTab === idx ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
                                        <div className="space-y-8">
                                            <div className="inline-flex items-center gap-4 px-5 py-2.5 rounded-2xl bg-white/10 backdrop-blur-xl border border-violet-400/25 text-white shadow-2xl">
                                                <div className="h-10 w-10 bg-violet-500 text-white rounded-xl flex items-center justify-center shadow-lg shadow-violet-500/40 ring-1 ring-violet-300/30">
                                                    {tool.icon}
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-black uppercase tracking-widest text-[10px] text-violet-300">Góc học tập</span>
                                                    <span className="font-black text-xl tracking-tight leading-none">{tool.title}</span>
                                                </div>
                                            </div>

                                            <h3 className="text-4xl md:text-5xl font-black text-white leading-[1.15] tracking-tight drop-shadow-2xl">
                                                {tool.title.includes('Mới') ? tool.title : `Sức mạnh AI cho ${tool.title.toLowerCase()}`}
                                            </h3>

                                            <p className="text-xl font-medium text-indigo-100/90 leading-relaxed max-w-xl drop-shadow-lg">
                                                {tool.description}
                                            </p>

                                            <div className="flex flex-wrap gap-4 pt-4">
                                                <button
                                                    type="button"
                                                    onClick={() => navigateWithLoginRedirect(tool.view)}
                                                    className="px-10 py-5 bg-violet-600 text-white font-black rounded-2xl shadow-2xl shadow-violet-600/45 hover:bg-violet-500 transition-all transform hover:scale-105 active:scale-95 flex items-center gap-3 text-lg ring-1 ring-violet-300/20"
                                                >
                                                    <SparklesIcon className="h-6 w-6" />
                                                    <span>Trải nghiệm ngay</span>
                                                </button>

                                                <div className="flex -space-x-3 items-center">
                                                    {studentSocialAvatars.map((src, i) => (
                                                        <div key={i} className="h-10 w-10 rounded-full border-2 border-indigo-950 bg-indigo-900/80 flex items-center justify-center overflow-hidden ring-1 ring-violet-500/20">
                                                            <img src={src} alt="" className="w-full h-full object-cover grayscale" />
                                                        </div>
                                                    ))}
                                                    <div className="pl-6 text-sm text-violet-200/70 font-bold uppercase tracking-widest">{studentSocialCaption}</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="hidden lg:block relative group perspective-1000">
                                            <div className="absolute -inset-20 bg-fuchsia-500/15 blur-[120px] rounded-full group-hover:bg-violet-500/25 transition-all duration-1000" />
                                            <div className="relative p-3 bg-white/5 backdrop-blur-3xl border border-violet-300/20 rounded-[40px] shadow-[0_40px_100px_rgba(76,29,149,0.35)] overflow-hidden transform rotate-2 group-hover:rotate-0 transition-transform duration-700">
                                                {tool.media && tool.media.length > 0 ? (
                                                    <img
                                                        src={resolvePublicFileUrl(tool.media[0])}
                                                        className="w-full h-[400px] object-cover rounded-[32px] shadow-inner"
                                                        alt=""
                                                    />
                                                ) : (
                                                    <div className="h-[400px] w-full flex items-center justify-center bg-indigo-950/90 rounded-[32px]">
                                                        {tool.icon}
                                                    </div>
                                                )}

                                                <div className="absolute top-8 right-8 bg-gradient-to-br from-fuchsia-500 to-violet-700 text-white px-4 py-2 rounded-2xl font-black text-xs uppercase tracking-widest shadow-2xl animate-bounce-slow ring-1 ring-white/20">
                                                    Học hay
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="relative z-30 shrink-0 min-w-0 bg-indigo-950/40 backdrop-blur-[32px] border-t border-violet-400/15 p-4">
                            <div
                                ref={studentTabStripRef}
                                className="homepage-tab-strip flex min-w-0 gap-4 overflow-x-auto overflow-y-hidden scroll-smooth overscroll-contain px-4 py-2 sm:px-6 max-w-7xl mx-auto items-center touch-pan-x scrollbar-hide"
                            >
                                {studentTools.map((tool, idx) => (
                                    <button
                                        key={tool.view}
                                        type="button"
                                        ref={(el) => {
                                            studentTabBtnRefs.current[idx] = el;
                                        }}
                                        onClick={() => setActiveStudentToolTab(idx)}
                                        className={`px-6 py-4 rounded-2xl transition-all flex items-center gap-4 shrink-0 border-2 ${
                                            activeStudentToolTab === idx
                                                ? 'bg-white border-violet-100 text-slate-900 shadow-[0_10px_40px_rgba(139,92,246,0.35)] scale-110 z-10'
                                                : 'bg-white/5 border-violet-400/10 text-violet-100/45 hover:bg-violet-500/10 hover:text-white hover:border-violet-300/25'
                                        }`}
                                    >
                                        <div className={`h-8 w-8 flex items-center justify-center rounded-xl transition-all ${activeStudentToolTab === idx ? 'bg-violet-100 text-violet-700' : 'bg-white/10 text-violet-200/50'}`}>
                                            {React.cloneElement<{ className?: string }>(tool.icon as React.ReactElement<{ className?: string }>, { className: 'h-5 w-5' })}
                                        </div>
                                        <div className="flex flex-col items-start">
                                            <span className="font-black text-sm whitespace-nowrap tracking-tight leading-none">{tool.title}</span>
                                            {activeStudentToolTab === idx && <div className="h-1 w-8 bg-violet-500 rounded-full mt-1.5 animate-grow-x" />}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            {/* Admin Mission Control Section — cuối trang chủ */}
            <section className="relative overflow-hidden rounded-[60px] border border-slate-100 bg-white p-6 shadow-sm md:p-8 lg:p-10">
                <div className="absolute top-0 right-0 p-12 opacity-[0.03]">
                    <BoltIcon className="h-96 w-96 text-slate-900" />
                </div>
                
                <div className="relative grid lg:grid-cols-[1.2fr,0.8fr] gap-20 items-center">
                    <div className="space-y-10">
                        <div className="space-y-4">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-indigo-600 text-[10px] font-black uppercase tracking-[0.2em]">
                                <BoltIcon className="h-4 w-4" />
                                <span>Quản trị vận hành</span>
                            </div>
                            <h2 className="text-4xl md:text-5xl font-black text-slate-900 leading-tight tracking-tight">Điều phối dạy và học <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-blue-600">rõ ràng và linh hoạt hơn</span></h2>
                            <p className="text-lg text-slate-500 font-medium max-w-xl">
                                Bên cạnh các công cụ tạo học liệu, {UI_CONFIG.APP_NAME} còn giúp bạn quản lý lớp học, theo dõi tiến độ và vận hành hoạt động dạy học gọn gàng hơn.
                            </p>
                        </div>
                        
                        <div className="grid sm:grid-cols-2 gap-8">
                            {[
                                { title: 'Quản lý Credit linh hoạt', desc: 'Thiết lập chi phí cho từng tính năng AI theo nhu cầu thực tế.', icon: <BoltIcon className="h-6 w-6" /> },
                                { title: 'Tùy cấu hình AI Multi-Model', desc: 'Chuyển đổi linh hoạt giữa Gemini Pro, GPT-4o và các model cao cấp khác.', icon: <SparklesIcon className="h-6 w-6" /> },
                                { title: 'Thanh toán & Gói cước', desc: 'Tích hợp VietQR, MoMo tự động kích hoạt gói cước ngay lập tức.', icon: <TrophyIcon className="h-6 w-6" /> },
                                { title: 'Quản lý Học viên 4.0', desc: 'Theo dõi tiến độ học tập chi tiết và quản lý hồ sơ không giới hạn.', icon: <UserGroupIcon className="h-6 w-6" /> }
                            ].map((item, i) => (
                                <div key={i} className="flex gap-5 group">
                                    <div className="h-14 w-14 shrink-0 rounded-[20px] bg-slate-50 border border-slate-100 flex items-center justify-center text-indigo-600 shadow-sm group-hover:bg-indigo-600 group-hover:text-white transition-all">
                                        {item.icon}
                                    </div>
                                    <div>
                                        <div className="font-black text-slate-800 text-lg tracking-tight">{item.title}</div>
                                        <p className="text-sm text-slate-500 mt-2 font-medium leading-relaxed">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        
                        <div className="pt-4">
                            <button onClick={handleAdminStart} className="px-10 py-5 bg-indigo-600 text-white font-black rounded-2xl shadow-2xl shadow-indigo-600/20 hover:bg-indigo-700 transition-all transform hover:scale-105 active:scale-95 flex items-center gap-3">
                                <span>Trải nghiệm Bảng quản trị</span>
                                <ChevronRightIcon className="h-6 w-6" />
                            </button>
                        </div>
                    </div>
                    
                    <div className="relative group">
                        <div className="absolute -inset-4 bg-indigo-600/5 rounded-[48px] blur-2xl group-hover:bg-indigo-600/10 transition-colors" />
                        <AdminMockup />
                        <div className="absolute -bottom-8 -right-8 p-8 bg-white rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.1)] border border-slate-100 scale-90 md:scale-100">
                            <div className="flex items-center gap-4">
                                <div className="h-14 w-14 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 shadow-inner">
                                    <CheckIcon className="h-8 w-8" />
                                </div>
                                <div>
                                    <div className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Status</div>
                                    <div className="text-xl font-black text-slate-800">Sẵn sàng 24/7</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            
            <AdSlot slot={(window as any).__EDUAPP_ADS__?.slots?.loading_banner} slotId="loading_banner" />
        </div>
    );
};

export default Dashboard;